import { PLTV_LANDINGS, PL_VALUE_LANDING } from '../constants';
import { parseOnboardingPath } from '../../locales/languageConfig';

export const PLTV_DEFAULT_VALUE = 45;

const DEFAULT_PREDICTED_LTV_PRICE = PLTV_DEFAULT_VALUE;

const LTV_DATA = {
  'main-3months-0day-all-all': 63,
  'main-1month-0day-googlepay-all': 34,
  'main-1month-0day-card-all': 38,
  'main-1month-0day-applepay-all': 34,

  'upsell-3months-0day-all-all': 13,
  'upsell-1month-0day-all-all': 17,

  'main-1month-3day-all-all': 45,

  'main-1month-3day-googlepay-US': 33,
  'main-1month-3day-googlepay-PE': 20,
  'main-1month-3day-googlepay-MX': 18,
  'main-1month-3day-googlepay-FR': 62,
  'main-1month-3day-googlepay-ES': 39,
  'main-1month-3day-googlepay-DE': 45,
  'main-1month-3day-googlepay-CA': 27,
  'main-1month-3day-googlepay-BR': 13,
  'main-1month-3day-googlepay-AT': 58,
  'main-1month-3day-googlepay-AR': 31,

  'main-1month-3day-card-UY': 50,
  'main-1month-3day-card-US': 30,
  'main-1month-3day-card-SV': 38,
  'main-1month-3day-card-PE': 24,
  'main-1month-3day-card-MX': 18,
  'main-1month-3day-card-IT': 55,
  'main-1month-3day-card-HN': 41,
  'main-1month-3day-card-GT': 43,
  'main-1month-3day-card-FR': 61,
  'main-1month-3day-card-ES': 39,
  'main-1month-3day-card-EC': 43,
  'main-1month-3day-card-DE': 51,
  'main-1month-3day-card-CR': 46,
  'main-1month-3day-card-CO': 24,
  'main-1month-3day-card-CH': 86,
  'main-1month-3day-card-CA': 32,
  'main-1month-3day-card-BR': 14,
  'main-1month-3day-card-AT': 67,
  'main-1month-3day-card-AR': 36,

  'main-1month-3day-applepay-US': 23,
  'main-1month-3day-applepay-MX': 25,
  'main-1month-3day-applepay-FR': 52,
  'main-1month-3day-applepay-ES': 39,
  'main-1month-3day-applepay-CH': 57,
  'main-1month-3day-applepay-CA': 23,
  'main-1month-3day-applepay-BR': 19,
  'main-1month-3day-applepay-AT': 74,
};

const parsePlanName = (planName: string) => {
  const name = planName.toLowerCase();

  const isUpsell = name.includes('upsell');

  let duration: '1month' | '3months';
  if (name.includes('3month') || name.includes('3-month')) {
    duration = '3months';
  } else {
    duration = '1month';
  }

  let trialDuration: '0day' | '3day' | 'unsupported';
  const introDaysMatch = name.match(/intro(\d+)d/);
  const dayTrialMatch = name.match(/(\d+)-day/);
  const isFullPeriodSubscription = name.includes('1-month-sub') || name.includes('3-month-sub');

  if (introDaysMatch) {
    trialDuration = introDaysMatch[1] === '3' ? '3day' : 'unsupported';
  } else if (dayTrialMatch) {
    trialDuration = dayTrialMatch[1] === '3' ? '3day' : 'unsupported';
  } else if (isFullPeriodSubscription) {
    trialDuration = '0day';
  } else if (name.includes('intro')) {
    trialDuration = '3day';
  } else {
    trialDuration = '0day';
  }

  return {
    type: isUpsell ? 'upsell' : 'main',
    duration,
    trialDuration,
  };
};

const normalizePaymentMethod = (paymentMethod: string): string => {
  const method = paymentMethod.toLowerCase();
  if (method.includes('google') || method.includes('googlepay')) return 'googlepay';
  if (method.includes('apple') || method.includes('applepay')) return 'applepay';
  if (method.includes('card')) return 'card';
  return 'all';
};

export const isUpsellGuidePlan = (plan: string): boolean => plan.toLowerCase().includes('guide');

export type PLTVCountrySource = 'geo' | 'link' | 'fallback';

export type PLTVCountry = {
  country: string | null;
  source: PLTVCountrySource;
};

export const resolvePLTVCountry = (
  geo: string | null | undefined,
  linkCountry: string | null | undefined,
): PLTVCountry => {
  if (geo) return { country: geo, source: 'geo' };
  if (linkCountry) return { country: linkCountry, source: 'link' };
  return { country: null, source: 'fallback' };
};

export const resolveLTVUserLocation = (userLocation: string | null | undefined): string => {
  const code = userLocation?.trim();
  return code || 'all';
};

const normalizeCountryCode = (country: string): string => {
  const countryCode = country.toUpperCase();
  const validCountries = [
    'US',
    'PE',
    'MX',
    'FR',
    'ES',
    'DE',
    'CA',
    'BR',
    'AT',
    'AR',
    'UY',
    'SV',
    'IT',
    'HN',
    'GT',
    'EC',
    'CR',
    'CO',
    'CH',
  ];

  if (validCountries.includes(countryCode)) {
    return countryCode;
  }
  return 'all';
};

export const getPredictedLTVPrice = (
  plan: string,
  paymentMethod: string,
  userLocation: string,
  isUpsell: 'upsell' | 'checkout' = 'checkout',
  useDefaultValue = false,
) => {
  try {
    if (useDefaultValue) {
      return DEFAULT_PREDICTED_LTV_PRICE;
    }

    const planInfo = parsePlanName(plan);
    if (planInfo.trialDuration === 'unsupported') {
      return DEFAULT_PREDICTED_LTV_PRICE;
    }

    const normalizedPaymentMethod = normalizePaymentMethod(paymentMethod);
    const normalizedCountry = normalizeCountryCode(userLocation);
    const type = isUpsell === 'upsell' ? 'upsell' : planInfo.type;

    const specificKey = `${type}-${planInfo.duration}-${planInfo.trialDuration}-${normalizedPaymentMethod}-${normalizedCountry}`;
    let ltvValue = LTV_DATA[specificKey as keyof typeof LTV_DATA];

    if (ltvValue === undefined) {
      const fallbackKey = `${type}-${planInfo.duration}-${planInfo.trialDuration}-all-${normalizedCountry}`;
      ltvValue = LTV_DATA[fallbackKey as keyof typeof LTV_DATA];
    }

    if (ltvValue === undefined) {
      const fallbackKey = `${type}-${planInfo.duration}-${planInfo.trialDuration}-${normalizedPaymentMethod}-all`;
      ltvValue = LTV_DATA[fallbackKey as keyof typeof LTV_DATA];
    }

    if (ltvValue === undefined) {
      const fallbackKey = `${type}-${planInfo.duration}-${planInfo.trialDuration}-all-all`;
      ltvValue = LTV_DATA[fallbackKey as keyof typeof LTV_DATA];
    }

    return ltvValue ?? DEFAULT_PREDICTED_LTV_PRICE;
  } catch (error) {
    console.error('Error calculating predicted LTV price:', error);
    return DEFAULT_PREDICTED_LTV_PRICE;
  }
};

export type ResolvePLTVPriceParams = {
  landing?: string | null;
  plan: string;
  paymentMethod?: string;
  userLocation?: string | null;
  useTimerDefault?: boolean;
};

const normalizeLanding = (landing?: string | null): string => (landing ?? '').trim().toLowerCase();

export const isPLTVEnabled = (landing?: string | null): boolean =>
  PLTV_LANDINGS.includes(normalizeLanding(landing));

export const isPLValueLanding = (landing?: string | null): boolean =>
  normalizeLanding(landing) === PL_VALUE_LANDING;

export const resolvePLTVPriceForFlow = ({
  landing,
  plan,
  paymentMethod = 'card',
  userLocation,
  useTimerDefault = false,
}: ResolvePLTVPriceParams): number | undefined => {
  if (!isPLTVEnabled(landing) || isUpsellGuidePlan(plan)) {
    return undefined;
  }

  const ltvScope: 'upsell' | 'checkout' = plan.toLowerCase().includes('upsell')
    ? 'upsell'
    : 'checkout';
  const { linkCountry } = parseOnboardingPath(window.location.pathname);
  const { country } = resolvePLTVCountry(userLocation, linkCountry);

  return getPredictedLTVPrice(
    plan,
    paymentMethod,
    resolveLTVUserLocation(country),
    ltvScope,
    useTimerDefault,
  );
};
